import { Button } from "@msuite/ui/button";
import { Checkbox } from "@msuite/ui/checkbox";
import { useTranslation } from "@msuite/ui/localization";
import { type AsyncModalController, Modal } from "@msuite/ui/modal";
import type { RiskAssessmentList } from "@msuite/utils/types";
import { useState } from "react";
import { AttendanceRiskAssessmentItem } from "./attendance-risk-assessment-item";

/** Props Interface */
interface AttendanceRiskAssessmentsModalProps {
	controller: AsyncModalController<boolean>;
	riskAssessments: RiskAssessmentList;
}

export function AttendanceRiskAssessmentsModal({
	controller,
	riskAssessments,
}: AttendanceRiskAssessmentsModalProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** State */
	// Every document in the list is pending by contract — the requirements leave out what this
	// person acknowledged within its interval — so the tick is the whole question the step asks.
	const [accepted, setAccepted] = useState(false);

	/** Render */
	return (
		<Modal
			isOpen={controller.isOpen}
			onClose={() => controller.onResolve(false)}
			title={t("users.risk_assessments")}
			withWarning={false}
		>
			<Modal.Body>
				<div className="v-stack gap-2">
					{riskAssessments.map((item) => (
						<AttendanceRiskAssessmentItem
							key={item.file.id}
							item={item}
						/>
					))}
				</div>
				<button
					type="button"
					onClick={() => setAccepted((prev) => !prev)}
					className="h-stack w-full items-center gap-3 mt-2 cursor-pointer"
				>
					<Checkbox
						value={accepted}
						onChange={setAccepted}
					/>
					<span className="text-sm">{t("users.i_accept_the_risk_assessments")}</span>
				</button>
			</Modal.Body>
			<Modal.Footer>
				<div className="self-end h-stack gap-4 ml-auto">
					<Button
						variant="ghost"
						onClick={() => {
							controller.onResolve(false);
						}}
					>
						{t("general.cancel")}
					</Button>
					<Button
						variant="primary"
						isDisabled={!accepted}
						onClick={() => {
							controller.onResolve(true);
						}}
					>
						{t("general.confirm")}
					</Button>
				</div>
			</Modal.Footer>
		</Modal>
	);
}
